import { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import {
  LandingHeader,
  MobileMenu,
  HeroSection,
  WhatIsBHG,
  OurStory,
  AtAGlance,
  CurrentCoursesSection,
  HowItWorksSection,
  HowToJoinSection,
  MissionSection,
  OurBoardSection,
  ResourcesSection,
  ContactSection,
  LandingFooter,
  ScheduleVisitModal,
  LoggedInPublicSiteBanner,
} from '@components/features/landing';

import { EVENTS } from '@constants/telemetryEvents';
import { VISIT_PATH, normalizePublicPath, sectionIdForLandingPath } from '@constants/publicSearch';
import { isPublicSiteViewSearch, withPublicSiteView } from '@constants/publicSiteView';
import { useAuth } from '@contexts/AuthContext';
import { useTheme } from '@contexts/ThemeContext';
import { useTelemetry } from '@hooks/useTelemetry';
import { useLandingData } from '@hooks/useLandingData';
import { getDefaultAppPath } from '@utils/postLoginRedirect';
import { usePendingFamilyRegistration } from '@hooks/usePendingFamilyRegistration';
import { scrollLandingToSectionId } from '@utils/landingScrollRestore';
import type { LegalPageLocationState } from '@utils/legalBackNavigation';

import './LandingPage.css';

/**
 * BHG Landing Page
 *
 * Landing page. Section links are real URLs (`/contact`, `/courses`, …) so
 * search engines can highlight them. Composed of section components.
 * Signed-in users are redirected into the app unless they open `/?view=public`.
 */
export function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, token, isRestored } = useAuth();
  const { pendingFamilyRegistration, isLoading: familyRegLoading } =
    usePendingFamilyRegistration();
  const { track } = useTelemetry();
  const hasTrackedView = useRef(false);
  const { isDark: isDarkMode, toggleTheme } = useTheme();
  const {
    activeSemester,
    courses,
    teacherCount,
    classCount,
    avgK12ClassesPerGrade,
    semesterLoading,
    coursesLoading,
  } = useLandingData();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const stateTarget = (location.state as LegalPageLocationState | null)?.scrollTo?.trim() ?? '';
  const hashTarget = location.hash.replace(/^#/, '').trim();
  const pathSectionId = sectionIdForLandingPath(location.pathname);
  const scrollTargetId = stateTarget || hashTarget || pathSectionId || '';
  const routeSectionId = hashTarget || pathSectionId;
  const locationKey = `${location.pathname}|${location.hash}|${stateTarget}`;
  const [spyKey, setSpyKey] = useState(locationKey);
  const [scrollSpyId, setScrollSpyId] = useState<string | null>(null);
  if (spyKey !== locationKey) {
    setSpyKey(locationKey);
    setScrollSpyId(null);
  }
  const activeSectionId = scrollSpyId ?? routeSectionId;
  const isVisitRequest = normalizePublicPath(location.pathname) === VISIT_PATH;
  const visitTracked = useRef(false);

  const viewingPublicSite =
    Boolean(user && token) && isPublicSiteViewSearch(searchParams.toString());

  /* Redirect: session but no 2FA → setup-2fa; pending family reg → form; else dashboard/enroll.
     Skip the final app redirect when explicitly previewing the public site. */
  useEffect(() => {
    if (!user || !token || familyRegLoading) return;
    if (!user.twoFactorEnabled) {
      navigate('/setup-2fa', { replace: true });
      return;
    }
    if (pendingFamilyRegistration) {
      navigate('/family-registration', { replace: true });
      return;
    }
    if (viewingPublicSite) return;
    let cancelled = false;
    void (async () => {
      const path = await getDefaultAppPath(token, user);
      if (!cancelled) navigate(path, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [user, token, navigate, familyRegLoading, pendingFamilyRegistration, viewingPublicSite]);

  useEffect(() => {
    /* Mobile = narrow + portrait (hamburger); 640px+ or landscape = tablet-style full nav (iPad Mini portrait = tablet) */
    const mediaQuery = window.matchMedia('(max-width: 639px) and (orientation: portrait)');
    const handleChange = () => setIsMobile(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  const HERO_SCROLL_THRESHOLD = 120;
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
      if (scrollPosition < HERO_SCROLL_THRESHOLD) {
        setScrollSpyId(null);
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sectionIds = ['about', 'mission', 'our-story', 'our-board', 'courses', 'how-to-join', 'resources', 'contact'];
    const headerOffset = 100;
    // One observer for every section (instead of one per section).
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && window.scrollY >= HERO_SCROLL_THRESHOLD) {
            setScrollSpyId(entry.target.id);
          }
        });
      },
      { rootMargin: `-${headerOffset}px 0px -60% 0px`, threshold: 0 }
    );
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (scrollTargetId) {
      scrollLandingToSectionId(scrollTargetId);
      return;
    }
    window.scrollTo(0, 0);
  }, [scrollTargetId]);

  useEffect(() => {
    if (!isVisitRequest) {
      visitTracked.current = false;
      return;
    }
    if (visitTracked.current) return;
    visitTracked.current = true;
    const source =
      (location.state as { visitSource?: string } | null)?.visitSource ?? 'direct';
    track(EVENTS.LANDING_SCHEDULE_VISIT_OPENED, { source });
  }, [isVisitRequest, location.state, track]);

  const closeVisitRequest = () => {
    if (location.key === 'default') {
      navigate(withPublicSiteView('/', viewingPublicSite), { replace: true });
      return;
    }
    navigate(-1);
  };
  useEffect(() => {
    if (!isRestored || hasTrackedView.current) return;
    hasTrackedView.current = true;
    track(EVENTS.LANDING_VIEWED, {
      isLoggedIn: String(Boolean(user)),
      publicSitePreview: String(viewingPublicSite),
    });
  }, [isRestored, user, track, viewingPublicSite]);

  // Track which sections users scroll through (landing page engagement depth)
  useEffect(() => {
    if (!activeSectionId) return;
    track(EVENTS.LANDING_SECTION_VIEWED, { section: activeSectionId });
  }, [activeSectionId, track]);

  return (
    <div className="bhg-landing-page">
      {viewingPublicSite ? <LoggedInPublicSiteBanner /> : null}

      <LandingHeader
        isScrolled={isScrolled}
        isMobile={isMobile}
        isMobileMenuOpen={isMobileMenuOpen}
        onOpenMobileMenu={() => setIsMobileMenuOpen(true)}
        activeSectionId={activeSectionId}
        viewingPublicSite={viewingPublicSite}
      />

      <MobileMenu
        isOpen={isMobileMenuOpen}
        isDarkMode={isDarkMode}
        onClose={() => setIsMobileMenuOpen(false)}
        onToggleTheme={toggleTheme}
        viewingPublicSite={viewingPublicSite}
      />

      <main role="main">
        <HeroSection
          firstDayLabel={activeSemester?.startDateFormatted}
          semesterLabel={activeSemester?.label}
          semesterLoading={semesterLoading}
        />

        <WhatIsBHG />

        <MissionSection />

        <OurStory />

        <AtAGlance
          avgK12ClassesPerGrade={avgK12ClassesPerGrade}
          tutorsCount={teacherCount}
          classesCount={classCount}
        />

        <OurBoardSection />

        <CurrentCoursesSection
          semesterLabel={activeSemester?.label ?? ''}
          semesterLabelLoading={semesterLoading}
          courses={courses}
          loading={coursesLoading}
          hasActiveSemester={activeSemester != null}
        />

        <HowItWorksSection />

        <HowToJoinSection />

        <ResourcesSection />

        <ContactSection />
      </main>

      <LandingFooter isDarkMode={isDarkMode} viewingPublicSite={viewingPublicSite} />
      <Outlet />

      <ScheduleVisitModal
        isOpen={isVisitRequest}
        onClose={closeVisitRequest}
        thursdays={activeSemester?.thursdays ?? []}
      />
    </div>
  );
}
